// frontend/src/components/ServiceType/ServiceTypeForm.tsx
// UNIFIED: Mobile-first form component (replaces modal)

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Loader2, AlertCircle } from 'lucide-react';
import { ServiceType, ServiceTypeFormData } from '@/lib/api/serviceTypeService';

interface ServiceTypeFormProps {
  serviceType?: ServiceType | null;
  categories: Array<{ category_id: number; name: string }>;
  isLoading?: boolean;
  onSubmit: (data: ServiceTypeFormData) => Promise<void>;
  onCancel: () => void;
  errors?: Record<string, string>;
}

const ServiceTypeForm: React.FC<ServiceTypeFormProps> = ({
  serviceType,
  categories,
  isLoading = false,
  onSubmit,
  onCancel,
  errors = {}
}) => {
  const [formData, setFormData] = useState<ServiceTypeFormData>({
    category_id: 0,
    name: '',
    description: '',
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load existing data when editing
  useEffect(() => {
    if (serviceType) {
      setFormData({
        category_id: serviceType.category_id,
        name: serviceType.name,
        description: serviceType.description || '',
        is_active: serviceType.is_active
      });
    }
  }, [serviceType]);

  // Form manages its own validation errors

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setIsSubmitting(true);

    // Client-side validation
    const validationErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      validationErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      validationErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.category_id || formData.category_id === 0) {
      validationErrors.category_id = 'Category is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = {
        category_id: Number(formData.category_id),
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        is_active: formData.is_active
      };

      await onSubmit(submitData);
    } catch (error: any) {
      setFormErrors(error.errors || { general: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ServiceTypeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-first sticky header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onCancel}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                disabled={isSubmitting}
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Back to Service Types</span>
              </button>
              <div className="ml-4 sm:ml-8">
                <h1 className="text-xl font-semibold text-gray-900">
                  {serviceType ? 'Edit Service Type' : 'Create Service Type'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-6">
                {/* General errors */}
                {formErrors.general && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{formErrors.general}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', Number(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.category_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting || isLoading}
                  >
                    <option value={0}>Select a category</option>
                    {categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category_id && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.category_id}</p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Type Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter service type name"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter description (optional)"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Active service types can be used when creating new services
                  </p>
                </div>
              </div>
            </div>

            {/* Form actions */}
            <div className="px-4 py-3 sm:px-6 bg-gray-50 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {serviceType ? 'Update' : 'Create'} Service Type
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceTypeForm;